//Import external libraries
import React, { useState, useEffect, useContext, useRef } from "react";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  GoogleMap,
  useJsApiLoader,
  InfoWindow,
  MarkerClusterer,
  Marker,
  StandaloneSearchBox,
} from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import Geocode from "react-geocode";

//Import internal services and resources
import {
  setLocationObject,
  setInitialState,
  setElevation,
} from "../../../actions/locationActions";
import { setSnackbar } from "../../../actions/snackbarActions";
import { filterContext } from "../../../utils/hooks/filterContext";
import { appCtx } from "../../../utils/hooks/appContext";
import { getProjects } from "../../../utils/services/projectService";
import { getUserLocation } from "../../../utils/services/userService";
import termovent from "./mapStyle";
import config from "../../../utils/config";

//import MUI components
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import useTheme from "@mui/material/styles/useTheme";

const containerStyle = {
  width: "100%",
  height: "270px",
};

const lib = ["places"]; //, "elevation"]; //, "visualization", "drawing", "geometry"
function MapboxAPI(props) {
  const { filterParameters, setFilterParameters } = useContext(filterContext);

  //Get values from redux
  let { address, city, country, countryShortCode, markerPosition, elevation } =
    useSelector((state) => state.location);

  //Functions for loading map from google maps api
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyCSQ1dLeEIgi-ZoltDc3lBau9CDsVh028s", //localhost
    // googleMapsApiKey: "AIzaSyAQH3rpJJJefHVbw8WQPp5xEdzSSq1hKwE", //Produkcija
    nonce: "map",
    libraries: lib,
  });

  Geocode.setApiKey("AIzaSyCSQ1dLeEIgi-ZoltDc3lBau9CDsVh028s"); //localhost
  // Geocode.setApiKey("AIzaSyAQH3rpJJJefHVbw8WQPp5xEdzSSq1hKwE"); //produkcija

  const [map, setMap] = React.useState(null);

  const onLoad = React.useCallback(function callback(map) {
    map.setZoom(props.zoom);
    setMap(map);

    /*const bounds = new window.google.maps.LatLngBounds(config.termoventHQ);
    map.fitBounds(bounds);*/
  }, []);

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);

  //Set searchBox
  const onSBLoad = (ref) => {
    setSearchBox(ref);
  };

  const getElevation = async (lat, lng) => {
    const elevator = new window.google.maps.ElevationService();

    return new Promise((resolve, reject) => {
      elevator.getElevationForLocations(
        { locations: [{ lat, lng }] },
        (results, status) => {
          if (status === "OK") {
            if (results[0]) {
              //alert(results[0].elevation);
              resolve(results[0].elevation); // Resolve with the elevation data
            } else {
              console.log("No elevation data found for this location.");
              resolve(null); // Resolve with null if no data found
            }
          } else {
            console.error("Elevation service failed due to:", status);
            reject(
              new Error(`Elevation service failed with status: ${status}`)
            ); // Reject on error
          }
        }
      );
    });
  };

  let cityGoogle, state;
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { setProjectId } = useContext(appCtx);
  const dispatch = useDispatch();
  const [searchBox, setSearchBox] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [rows, setRows] = useState(props.projects);
  const [searchText, setSearchedText] = useState("");

  function filterItems() {
    if (
      filterParameters.length !== 0 &&
      filterParameters.items[0].value !== undefined &&
      filterParameters.items[0].value !== null &&
      (filterParameters.items[0].value !== "" ||
        filterParameters.items[0].value.length !== 0)
    ) {
      const filteredItems = props.projects.filter((item) => {
        if (filterParameters.items[0].operatorValue === "isAnyOf") {
          return filterParameters.items[0].value.includes(item.id.toString());
        } else if (filterParameters.items[0].columnField === "createdAt") {
          // Convert the date strings to Date objects
          const dateFilter = filterParameters.items[0].value;
          const dateDatabase = item[filterParameters.items[0].columnField];

          const date1 = new Date(dateFilter).toISOString().split("T")[0];
          const date2 = new Date(dateDatabase).toISOString().split("T")[0];

          switch (filterParameters.items[0].operatorValue) {
            case "is":
              return date1 === date2;
            case "before":
              return date1 > date2;
            case "onOrBefore":
              return date1 >= date2;
            case "after":
              return date1 < date2;
            case "onOrAfter":
              return date1 <= date2;
            case "not":
              return date1 !== date2;
          }
        } else if (
          filterParameters.items[0].columnField === "totalPrice" ||
          filterParameters.items[0].columnField === "numberOfUnits"
        ) {
          switch (filterParameters.items[0].operatorValue) {
            case "=":
              return (
                item[filterParameters.items[0].columnField] ==
                filterParameters.items[0].value
              );
            case ">=":
              return (
                item[filterParameters.items[0].columnField] >=
                filterParameters.items[0].value
              );
            case "<=":
              return (
                item[filterParameters.items[0].columnField] <=
                filterParameters.items[0].value
              );
            case "!=":
              return (
                item[filterParameters.items[0].columnField] !=
                filterParameters.items[0].value
              );
            case ">":
              return (
                item[filterParameters.items[0].columnField] >
                filterParameters.items[0].value
              );
            case "<":
              return (
                item[filterParameters.items[0].columnField] <
                filterParameters.items[0].value
              );
          }
        } else {
          return item[filterParameters.items[0].columnField]
            .toString()
            .toUpperCase()
            .includes(filterParameters.items[0].value.toUpperCase());
        }
      });

      setRows(filteredItems);
    } else {
      return;
      //setRows(props.projects);
      //if (map !== null) map.setZoom(6);
    }
  }

  useEffect(() => {
    if (props.componentDisplay === "projectsMap") {
      setRows(props.projects);

      setTimeout(filterItems(), [5000]);
    }
  }, [filterParameters]); //props.projects, visibleProjects,

  //Navigate to project page
  const toProjectInfo = (id) => {
    setProjectId(id);
    navigate("/project-item", {
      state: {
        projectId: id,
      },
    });
    window.scrollTo(0, 0);
  };

  const [currentZOom, setCurrentZOom] = useState(null);
  const [sameLocationClusterZoom, setSameLocationClusterZoom] = useState(null);

  useEffect(() => {
    if (map !== null) setCurrentZOom(map.getZoom());
  }, []);

  //Get route, house number, country and countryShortCode from coordinates
  const getLocationData = async (response) => {
    // console.log(response);
    let streetNumberAPI;
    let routeAPI;
    let cityAPI;
    let countryAPI;
    let countryShortCodeAPI;
    let latitudeAPI = response.results[0].geometry.location.lat;
    let longitudeAPI = response.results[0].geometry.location.lng;

    const elevationGoogle = await getElevation(latitudeAPI, longitudeAPI);

    for (let i = 0; i < response.results[0].address_components.length; i++) {
      for (
        let j = 0;
        j < response.results[0].address_components[i].types.length;
        j++
      ) {
        switch (response.results[0].address_components[i].types[j]) {
          case "locality":
            cityGoogle = response.results[0].address_components[i].long_name;
            cityAPI = response.results[0].address_components[i].long_name;

            break;
          case "postal_town":
            cityGoogle = response.results[0].address_components[i].long_name;
            cityAPI = response.results[0].address_components[i].long_name;

            break;
          case "administrative_area_level_3":
            cityGoogle = response.results[0].address_components[i].long_name;
            cityAPI = response.results[0].address_components[i].long_name;
            break;
          case "administrative_area_level_4":
            cityGoogle = response.results[0].address_components[i].long_name;
            cityAPI = response.results[0].address_components[i].long_name;
            break;
          case "street_number":
            streetNumberAPI =
              response.results[0].address_components[i].long_name;

            break;
          case "route":
            routeAPI = response.results[0].address_components[i].long_name;

            break;
          case "administrative_area_level_1":
            state = response.results[0].address_components[i].long_name;
            break;
          case "country":
            countryAPI = response.results[0].address_components[i].long_name;
            countryShortCodeAPI =
              response.results[0].address_components[i].short_name;

            break;
        }
      }
    }

    dispatch(
      setLocationObject({
        address: routeAPI + " " + streetNumberAPI,
        city: cityAPI,
        country: countryAPI,
        countryShortCode: countryShortCodeAPI,
        markerPosition: { lat: latitudeAPI, lng: longitudeAPI },
        elevation: Math.round(elevationGoogle),
      })
    );
  };
  const loadUserIPLocation = async () => {
    const { latitude: lat, longitude: lng } = await getUserLocation(
      localStorage.getItem("jwt")
    );

    if (lat !== 0 && lng !== 0) {
      Geocode.fromLatLng(lat, lng).then(
        (response) => {
          setSearchedText(response.results[0].formatted_address);

          getLocationData(response);
        },
        (error) => {
          console.error(error);
        }
      );
      //   dispatch(setLocationMarkerPosition({ lat, lng }));
    } else dispatch(setInitialState());
  };
  //Call function for temperature, air quality and electricity every time location has been changed
  useEffect(() => {
    if (props.componentDisplay === "newProject") {
      loadUserIPLocation();
    }
  }, []);

  useEffect(() => {
    if (props.componentDisplay === "newProject") {
      if (city !== null && city !== undefined)
        props.handleLocation(
          address,
          city,
          country,
          countryShortCode,
          markerPosition,
          elevation
        );
      else {
        dispatch(setInitialState());
        dispatch(setSnackbar(true, "error", t("snackbar.mandatoryCity")));
      }
    }
  }, [countryShortCode, markerPosition, elevation]);

  //Function for changing location by moving marker on map
  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    Geocode.fromLatLng(lat, lng).then(
      (response) => {
        /*dispatch(setLocationMarkerPosition({ lat, lng }));*/

        //!!! VRATIII dispatch(setLocationAddress(response.results[0].formatted_address));
        setSearchedText(response.results[0].formatted_address);

        getLocationData(response);
      },
      (error) => {
        console.error(error);
      }
    );
  };
  //Function for changing location by typing in select box
  const onPlacesChanged = () => {
    const googleResults = searchBox.getPlaces();

    let headers = new Headers({
      "Accept-Encoding": "gzip, deflate, br",
      "Content-Type": "application/json",
    });

    /*if (googleResults[0].place_id !== null) {
      fetch(
        `https://maps.googleapis.com/maps/api/place/details/json?place_id=${googleResults[0].place_id}&key=AIzaSyAvuT5vx21LcbzOu-1FFt2kCR4RjdGiluU`,
        { mode: "no-cors" }
      ).then((placeDetails) => {
        console.log(placeDetails);
      });
    }*/
    if (googleResults.length !== 0) {
      Geocode.fromAddress(googleResults[0].formatted_address).then(
        (response) => {
          /*dispatch(
          setLocationMarkerPosition({
            lat: response.results[0].geometry.location.lat,
            lng: response.results[0].geometry.location.lng,
          })
        );
        dispatch(setLocationAddress(response.results[0].formatted_address));*/
          setSearchedText(response.results[0].formatted_address);

          getLocationData(response);
        }
      );
    } else {
      dispatch(setSnackbar(true, "error", t("snackbar.invalidAddress")));
    }
  };
  const clusterOptions = {
    imagePath:
      "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
    // styles: [
    //   {
    //     // textColor: "white",
    //   },
    // ],
  };

  const options = {
    styles: [
      {
        // textColor: 'white',
        url: "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m1.png",
        height: 60,
        width: 60,
        textSize: 18,
        anchor: [28, 0],
        textColor: theme.palette.primary.light,
        backgroundColor: theme.palette.secondary.main,
        iconAnchor: [22, 53],
        backgroundPosition: "0 0",
      },
    ],
  };

  //ne radi!

  const iconStyles = [
    {
      url: "data:image/jpeg;base64,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",
      color: theme.palette.primary.light,
    },
    {
      url: "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAkGBw8PEhANEA4QEBIQDQ8PDQ0QDxAODhAQFREWFhURExUYHSggGBolGxUVITEhJSkrLi4uFx85ODM4NygtLisBCgoKDg0OGxAQGi0fHx8tLSstKy0tLSsrLS0tKysrLS0tLS0tLS0tLS0tLS0tLSstLTctLS0rLS03LS0tLSstLf/AABEIAKwBJgMBEQACEQEDEQH/xAAbAAEAAgMBAQAAAAAAAAAAAAAAAwUCBAYBB//EADUQAAIBAwIEBAUCBQUBAAAAAAABAgMEEQUhEjFBUQYiYXETMkJSgQeRFSNyobEUM1Nighb/xAAbAQEAAwEBAQEAAAAAAAAAAAAAAQIDBAUGB//EACkRAQACAgICAAYCAwEBAAAAAAABAgMRBDESIQUTIjJBURRhQnGBBhX/2gAMAwEAAhEDEQA/APtIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGQPOJBOnuQgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAxlNIJiJlDK47FdtIx/tG6rZG14pDHifcJ1Blg9HE+4NQyjVaG0TSEkbjuTtScf6TRmmTtnNZhkSgAAAAAAAAAAAAAAAAAAAAAAAAAAAAA8bAgqVuxWZa1p+0LeSGjKNJv2GkTaIYzq0ofNIbiExXJbqEM9SprkskecNI42Se2H8Vj9iI+ZB/Ev+2cdSpvmsE+cE8bJHUpoVaU/lkTuJZzTJXuGUqTXqNKxaGKeAslp1+jJiWdqfpsJlmT0AAAAAAAAAAAAAAAAAAAAAAAAAAAGtXk+RWW1IhHCDZC0zpHXu4U/V+hE2iF6YrZP6hWXF9OfXC7LYzm0y7aYKUa8U5cssq1mYj+k8LGo/oZbxllbPSPyy/h9TsPCUfyKMZ2FVfQx4ymORSfyglFx55RXprExPSe3vZw5PK7PctFphlfBS6zt7yFTZ7M0i0S474bU66Szg0Szids6EnnBMK3iNNosxAAAAAAAAAAAAAAAAAAAAAAAAAAAxnBMjSYnSr1K7cPJFY7yM7216dvHxRf6pVOHJ9WzLt3+qw23bU6UfiVp4XPhT3/YvqI92YfMvknxxRtW3XiaEdqFOLX3SW5nOeI+2HXj+F2t7y2VdfXq8vrcfRMynLZ3V+H4YjUxtB/Fq/wDyS/cj5lv20/h4evFPQ16vH63L0bJjNZnb4fhnqNLO18TxltXpxx3SyzWM8f5Q4cnwq0TvFZZRoUqy46E89eFvf9jTUWjdXHOS+K3hlhquLi+qZTp0RMWhaabeOX8uSyuj6mtLb9OHkYor9ULaEEjTThm0yyJQAAAAAAAAAAAAAAAAAAAAAAAAAAAAgu7aNRYfPoyJjbTHkmk7hRajeQso7Lim+T7GF7Rjh6nHw25du9RDj7y8qVpcdSTe+y7ehyWtNu30GLDTFGqw2bHR61VcSjwx+57Fq45ljm5mPH63uWzOzs6e1S6Sa5rBbxpHcsYzcnJ9mNF/rNMzwfHjy54I8sXW1/k8/Xl4JIWllU2p3SbfJYJ8aT1Ks5eVj+/GgvdGrUlxY4o/ctytscw0w8zHknXUtS0up0pcdOTiytbTHTfLhpljVo26/TL6N7HDXDUit33OuloyPA5OC3EncT6lfWdoqa259WbVrp5eXLOSdy2CzIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAaGraZC4g4tb/AEspekWj26eNyb4Lbq5KpZULBSrXU4trPw6SfmfZ4OWa1x+7Pepnzc2Yx4I1+5cTrvjG4uW1B/Cp/TGPlePXBxZOTa3Xp9Lwvg2HBG7fVP8AbnZ1ZS3cpP3bZz7l69aVjqGAWZQqyW6k17NoblWaVnuHQaH4vubZpN/EhylGfmePTJ0Y+RajyuZ8HwZ43Ean+nb0qFvqMY1rWcYzeHOlJ7+uEdsVrljdXzF82bgXnHnjcfiXXaPpcLeKSXm+pnVSkVh4XK5V89tz0sC7lAAAAAAAAAAAAAAAAAAAAAAAAAB5KWNwmI2wjVTI2maTDPiXdEo1L0IANPVtQhbUp1ptYis47lL3isblvxuPbPkjHXuXwjxFrNS9rSrTe3KC9Oh4ubLOS25fp/w7gU4eLwr3+VWZO8CQAACFjoWrVLSrGtB8n5l0aNMWScdtw4ufwqcvFOO77voupQuqUK0X8y39z28d4vXcPzDl8a3Hyzjt+G+XcwAA8bAJgegAAAAAAAAAAAAAAAAAAAAhuXsRLTHHtDRW5WGl+lTf15fEk0+uxnafbuwY4+XG2xG/lToupJ554J85iu2U8et8sUho2Xi2M3GEqbWduLJWvIie3Rm+EXrHlWduc/VXVXiFCPX5vZnNzcnrxex/5rhx5Tll8zPOfaASAAAAIAPpP6Uam8zt3ySXCejwsn+L43/0/Ej1ljt2934ht6eUpqUotpx9TstlrD5nF8PzZPetQzWqcdP4sUT57jcI/izXJ4Wa1vqE5Tim9slYvMy2vxqVpOllWe5eXJTpNb8vyWhnk7SkqAAAAAAAAAAAAAAAAAAAAQXXQrLXGjo9f6WITdQXDzJ+5hPb1cf2w91yWLWK7zGT7E8ON8mXK6dDiqQXqv8AJy0j29vkW1jlT/qTVbunHooxx+xly5+t6H/n6xHG25M5XvASAAAAAB1X6c1GruCXKWzOniz9bwvj9YnizP6Xmr0uGrV25zk/7m94+qXmcW/lir/p0vh+Wbaa7YOjF9jxudGuREsqHzL3JjtF/tX9XlH2NnlV7lNbcvyy0M8naUlQAAAAAAAAAAAAAAAAAAACC66FZa40dHr/AEsQm6guViT9zCe3q4/tg1xZtYv/ALsZPsOHMxyphy+nT4akH6r/ACc1J9vc5Fd45U36kU2rpyxs4xw/wY8uPrej8AtE8bTkzle6BIAAAAgCXU/pzTbu4NcovMvQ6eLH1vC+P2iOLMftfaxU4qtR9pyX9ze8/VLy+LXxxV/06Tw/HFtN+x0YvsePzp3yIhlQ+Ze5Mdov9q/q8o+xs8qvcprbl+WWhnk7SkqAAAAAAAAAAAAAAAAAAAAQ3K2Ilpj7Q0XuVhpfpT6lDFSXbOxlePbv41vLHDJQ+JQqQ+2LkhrddHl8vPW37cTBuMk+zRx9S+kmPKr3x/afFo0buO/Pi9EkOTXyrFkfBM3ys1sMvn5wvqwJAAAAACHf/p5Z/Dp1ruW3l8nqd3FrqJs+V+O5vmZKYY/69qy45OX3Sb/cT7lFY8axH6dnRh8O3pr7o5Z2R6q+dvbz5Ez+mNjHM4++5Fe057apK7rc/Y2l5tOk1vy/JaGeTtKSoAAAAAAAAAAAAAAAAAAABhVWUyJWrPtprYq37aus0sqNRf8Aopkj8t+JbUzWWpp9Xhlh8peV+xWs+3Rnp5V3HcOc8RWLo1X9s25RfTBz5aeNnrfD+R83FET3CTR6lOrCdlW+WquGEvtZNJi0eM/lHKrfFeM+Puvb594g0apZ1ZUpxfDlunPpKPTc4MuOaW1L6vg82nKxRas+/wAwrDN3AAAEAFhoek1burGlTjnfzS6JdcsvjxzedQ5Oby8fGxze8vo2qzp29OFjR5QXnl3yehfVY8IfI8aL5sk8jJ+empodm61WMUtotSlnlhFcdfKzXm54xYpn8z06nUaicuCPyx2R03n28Xj1mK+U9y2NGpbym+SWz9S2OPyx5l+qw3ZPLyXc8em3RWEi0MLdsyVQAAAAAAAAAAAAAAAAAAAAGnVjhlJdFZ3A4qcXB9VsO40b8bRaFBVg4tx7PYwmNPUraLRtPcW8buk6b2qRXkl1wi0x510yx3njZfOOp7cZWpSpycJJqSZyTExL6Ol63ruPcStVXo3tL/S3WMr/AG6r5rsabrkjxs4fDLxMnzcHX5hx2t+D7i3bcE6tPpUXb2OTJx7V69voeH8Zw5o1b6bfpzkoNbNNfg59PXi0T08wE7eqLeyT/YaRNoh0OieELm5abi6dPnKo+i74N8fHtZ5PM+MYMEaidz+na0qlDT6bt7ZKU2v5lbq2dsTXFGqvnLVzc7J8zN6j8Qp4QlUlhZlKTMfcy75mtK7n1EOzsbWNpSxzqzW76pPodla+Ff7fO58k8rLv/GEEIuTS5tsr21mYrG1/Th8OCgvybxGo08u1vO82kpxywi06hul3OAAAAAAAAAAAAAAAAAAAAAARV4ZWSJXpbUtaLwVbT7a+pW3GviRW/Urau/bXj5fGfGVVTm4vK5oyidO+1YtGpS6hYQvI5WI1Uvwy1qxeP7ZYM9+LbU+6uRu7SpRk4zi4tPZ8s+qOS1ZrPt9BizUy13Wdtiz1erS2TUl2l5kWrkmGWXh48n9f6dRZ6PQu6SqVaMVJt54YqJ0xjreu5h4WTm5uNlmtLeoP/iLL7GP4tFv/ALnK/aO/0u2safHTop46uKZFsdccbiF8HLz8zJ43s5281mrV22gu0PLsc9sky9bFwseP33P9tS2tp1ZcMIuTfbcrETM+nRkyUx13adOt03TYWkeOeJVWtvRnVSkUjc9vB5HJvyreNfVWFaq5vifUiZ2mtYrGoWml23CviS/BrSuvbi5GXynxhsyeSzGI02KEMbloZXttMSzAAAAAAAAAAAAAAAAAAAAAAAGrWp43KzDaltsITx7dUQtau2nfafnzw/KK2p+YdGDka+mysTcX1TRl07dRaG27inVXDWgn0U+bL7i3bCMd8c7xT/xXXHhmE2nSnndPD26mc4Ynp10+J2pGskOttqShGMF0SR1RGo08HJfztNv2lJUaWsWvxqU6X3Ipkr5V06OLl+Vli/6cxa+GIx3qzwvR5OaMGu3t5Pik29Y4WUKtKguGjBZ5ceMM1iYr6hw2pkzT5ZJ/41Zycnl5bZTt0REVj0sbGw+uey6I0rT8y483I/xq35zzstkuSLuaI09o087kxCL202yzAAAAAAAAAAAAAAAAAAAAAAAAAPGsghq1aePYrMN622xhNohMxtHcWkKu/wAsu5E1iV6ZrY/7hV3FjUh0bXczmsw7ceel0mkwbmn2Jp2rybRFHQm7yQDxghzN4mpNNvmc1u3tYpiaxL2hZznyi8dyYrMovnpTuVpb2UKW780jSKxDhyZ7ZOvUJZzbJUiNMqVPPsTEItbTaisFmMzt6EAAAAAAAAAAAAAAAAAAAAAAAAAA8aAgqUeqKzDWt/2hawQ07ZRqvk916jas0hNQhHmopFoZ2mekxKgAA1a1KCfE45ZWYhtW1pjW3jq9Ekl6bDaYp+2C3IW6TU6PVkxDO1/0nSwWZPQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGMoJjSYmYQyt+xXTSMianHCJhnadyyJQAAIq1NvkRML0tphG37kaWnJ+k0YJFtM5tMsggAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/2Q==",
      color: theme.palette.primary.light,
    },
    {
      url: "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAkGBw8PEhANEA4QEBIQDQ8PDQ0QDxAODhAQFREWFhURExUYHSggGBolGxUVITEhJSkrLi4uFx85ODM4NygtLisBCgoKDg0OGxAQGi0fHx8tLSstKy0tLSsrLS0tKysrLS0tLS0tLS0tLS0tLS0tLSstLTctLS0rLS03LS0tLSstLf/AABEIAKwBJgMBEQACEQEDEQH/xAAbAAEAAgMBAQAAAAAAAAAAAAAAAwUCBAYBB//EADUQAAIBAwIEBAUCBQUBAAAAAAABAgMEEQUhEjFBUQYiYXETMkJSgQeRFSNyobEUM1Nighb/xAAbAQEAAwEBAQEAAAAAAAAAAAAAAQIDBAUGB//EACkRAQACAgICAAYCAwEBAAAAAAABAgMRBDESIQUTIjJBURRhQnGBBhX/2gAMAwEAAhEDEQA/APtIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGQPOJBOnuQgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAxlNIJiJlDK47FdtIx/tG6rZG14pDHifcJ1Blg9HE+4NQyjVaG0TSEkbjuTtScf6TRmmTtnNZhkSgAAAAAAAAAAAAAAAAAAAAAAAAAAAAA8bAgqVuxWZa1p+0LeSGjKNJv2GkTaIYzq0ofNIbiExXJbqEM9SprkskecNI42Se2H8Vj9iI+ZB/Ev+2cdSpvmsE+cE8bJHUpoVaU/lkTuJZzTJXuGUqTXqNKxaGKeAslp1+jJiWdqfpsJlmT0AAAAAAAAAAAAAAAAAAAAAAAAAAAGtXk+RWW1IhHCDZC0zpHXu4U/V+hE2iF6YrZP6hWXF9OfXC7LYzm0y7aYKUa8U5cssq1mYj+k8LGo/oZbxllbPSPyy/h9TsPCUfyKMZ2FVfQx4ymORSfyglFx55RXprExPSe3vZw5PK7PctFphlfBS6zt7yFTZ7M0i0S474bU66Szg0Szids6EnnBMK3iNNosxAAAAAAAAAAAAAAAAAAAAAAAAAAAxnBMjSYnSr1K7cPJFY7yM7216dvHxRf6pVOHJ9WzLt3+qw23bU6UfiVp4XPhT3/YvqI92YfMvknxxRtW3XiaEdqFOLX3SW5nOeI+2HXj+F2t7y2VdfXq8vrcfRMynLZ3V+H4YjUxtB/Fq/wDyS/cj5lv20/h4evFPQ16vH63L0bJjNZnb4fhnqNLO18TxltXpxx3SyzWM8f5Q4cnwq0TvFZZRoUqy46E89eFvf9jTUWjdXHOS+K3hlhquLi+qZTp0RMWhaabeOX8uSyuj6mtLb9OHkYor9ULaEEjTThm0yyJQAAAAAAAAAAAAAAAAAAAAAAAAAAAAgu7aNRYfPoyJjbTHkmk7hRajeQso7Lim+T7GF7Rjh6nHw25du9RDj7y8qVpcdSTe+y7ehyWtNu30GLDTFGqw2bHR61VcSjwx+57Fq45ljm5mPH63uWzOzs6e1S6Sa5rBbxpHcsYzcnJ9mNF/rNMzwfHjy54I8sXW1/k8/Xl4JIWllU2p3SbfJYJ8aT1Ks5eVj+/GgvdGrUlxY4o/ctytscw0w8zHknXUtS0up0pcdOTiytbTHTfLhpljVo26/TL6N7HDXDUit33OuloyPA5OC3EncT6lfWdoqa259WbVrp5eXLOSdy2CzIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAaGraZC4g4tb/AEspekWj26eNyb4Lbq5KpZULBSrXU4trPw6SfmfZ4OWa1x+7Pepnzc2Yx4I1+5cTrvjG4uW1B/Cp/TGPlePXBxZOTa3Xp9Lwvg2HBG7fVP8AbnZ1ZS3cpP3bZz7l69aVjqGAWZQqyW6k17NoblWaVnuHQaH4vubZpN/EhylGfmePTJ0Y+RajyuZ8HwZ43Ean+nb0qFvqMY1rWcYzeHOlJ7+uEdsVrljdXzF82bgXnHnjcfiXXaPpcLeKSXm+pnVSkVh4XK5V89tz0sC7lAAAAAAAAAAAAAAAAAAAAAAAAAB5KWNwmI2wjVTI2maTDPiXdEo1L0IANPVtQhbUp1ptYis47lL3isblvxuPbPkjHXuXwjxFrNS9rSrTe3KC9Oh4ubLOS25fp/w7gU4eLwr3+VWZO8CQAACFjoWrVLSrGtB8n5l0aNMWScdtw4ufwqcvFOO77voupQuqUK0X8y39z28d4vXcPzDl8a3Hyzjt+G+XcwAA8bAJgegAAAAAAAAAAAAAAAAAAAAhuXsRLTHHtDRW5WGl+lTf15fEk0+uxnafbuwY4+XG2xG/lToupJ554J85iu2U8et8sUho2Xi2M3GEqbWduLJWvIie3Rm+EXrHlWduc/VXVXiFCPX5vZnNzcnrxex/5rhx5Tll8zPOfaASAAAAIAPpP6Uam8zt3ySXCejwsn+L43/0/Ej1ljt2934ht6eUpqUotpx9TstlrD5nF8PzZPetQzWqcdP4sUT57jcI/izXJ4Wa1vqE5Tim9slYvMy2vxqVpOllWe5eXJTpNb8vyWhnk7SkqAAAAAAAAAAAAAAAAAAAAQXXQrLXGjo9f6WITdQXDzJ+5hPb1cf2w91yWLWK7zGT7E8ON8mXK6dDiqQXqv8AJy0j29vkW1jlT/qTVbunHooxx+xly5+t6H/n6xHG25M5XvASAAAAAB1X6c1GruCXKWzOniz9bwvj9YnizP6Xmr0uGrV25zk/7m94+qXmcW/lir/p0vh+Wbaa7YOjF9jxudGuREsqHzL3JjtF/tX9XlH2NnlV7lNbcvyy0M8naUlQAAAAAAAAAAAAAAAAAAACC66FZa40dHr/AEsQm6guViT9zCe3q4/tg1xZtYv/ALsZPsOHMxyphy+nT4akH6r/ACc1J9vc5Fd45U36kU2rpyxs4xw/wY8uPrej8AtE8bTkzle6BIAAAAgCXU/pzTbu4NcovMvQ6eLH1vC+P2iOLMftfaxU4qtR9pyX9ze8/VLy+LXxxV/06Tw/HFtN+x0YvsePzp3yIhlQ+Ze5Mdov9q/q8o+xs8qvcprbl+WWhnk7SkqAAAAAAAAAAAAAAAAAAAAQ3K2Ilpj7Q0XuVhpfpT6lDFSXbOxlePbv41vLHDJQ+JQqQ+2LkhrddHl8vPW37cTBuMk+zRx9S+kmPKr3x/afFo0buO/Pi9EkOTXyrFkfBM3ys1sMvn5wvqwJAAAAACHf/p5Z/Dp1ruW3l8nqd3FrqJs+V+O5vmZKYY/69qy45OX3Sb/cT7lFY8axH6dnRh8O3pr7o5Z2R6q+dvbz5Ez+mNjHM4++5Fe057apK7rc/Y2l5tOk1vy/JaGeTtKSoAAAAAAAAAAAAAAAAAAABhVWUyJWrPtprYq37aus0sqNRf8Aopkj8t+JbUzWWpp9Xhlh8peV+xWs+3Rnp5V3HcOc8RWLo1X9s25RfTBz5aeNnrfD+R83FET3CTR6lOrCdlW+WquGEvtZNJi0eM/lHKrfFeM+Puvb594g0apZ1ZUpxfDlunPpKPTc4MuOaW1L6vg82nKxRas+/wAwrDN3AAAEAFhoek1burGlTjnfzS6JdcsvjxzedQ5Oby8fGxze8vo2qzp29OFjR5QXnl3yehfVY8IfI8aL5sk8jJ+empodm61WMUtotSlnlhFcdfKzXm54xYpn8z06nUaicuCPyx2R03n28Xj1mK+U9y2NGpbym+SWz9S2OPyx5l+qw3ZPLyXc8em3RWEi0MLdsyVQAAAAAAAAAAAAAAAAAAAAGnVjhlJdFZ3A4qcXB9VsO40b8bRaFBVg4tx7PYwmNPUraLRtPcW8buk6b2qRXkl1wi0x510yx3njZfOOp7cZWpSpycJJqSZyTExL6Ol63ruPcStVXo3tL/S3WMr/AG6r5rsabrkjxs4fDLxMnzcHX5hx2t+D7i3bcE6tPpUXb2OTJx7V69voeH8Zw5o1b6bfpzkoNbNNfg59PXi0T08wE7eqLeyT/YaRNoh0OieELm5abi6dPnKo+i74N8fHtZ5PM+MYMEaidz+na0qlDT6bt7ZKU2v5lbq2dsTXFGqvnLVzc7J8zN6j8Qp4QlUlhZlKTMfcy75mtK7n1EOzsbWNpSxzqzW76pPodla+Ff7fO58k8rLv/GEEIuTS5tsr21mYrG1/Th8OCgvybxGo08u1vO82kpxywi06hul3OAAAAAAAAAAAAAAAAAAAAAARV4ZWSJXpbUtaLwVbT7a+pW3GviRW/Urau/bXj5fGfGVVTm4vK5oyidO+1YtGpS6hYQvI5WI1Uvwy1qxeP7ZYM9+LbU+6uRu7SpRk4zi4tPZ8s+qOS1ZrPt9BizUy13Wdtiz1erS2TUl2l5kWrkmGWXh48n9f6dRZ6PQu6SqVaMVJt54YqJ0xjreu5h4WTm5uNlmtLeoP/iLL7GP4tFv/ALnK/aO/0u2safHTop46uKZFsdccbiF8HLz8zJ43s5281mrV22gu0PLsc9sky9bFwseP33P9tS2tp1ZcMIuTfbcrETM+nRkyUx13adOt03TYWkeOeJVWtvRnVSkUjc9vB5HJvyreNfVWFaq5vifUiZ2mtYrGoWml23CviS/BrSuvbi5GXynxhsyeSzGI02KEMbloZXttMSzAAAAAAAAAAAAAAAAAAAAAAAGrWp43KzDaltsITx7dUQtau2nfafnzw/KK2p+YdGDka+mysTcX1TRl07dRaG27inVXDWgn0U+bL7i3bCMd8c7xT/xXXHhmE2nSnndPD26mc4Ynp10+J2pGskOttqShGMF0SR1RGo08HJfztNv2lJUaWsWvxqU6X3Ipkr5V06OLl+Vli/6cxa+GIx3qzwvR5OaMGu3t5Pik29Y4WUKtKguGjBZ5ceMM1iYr6hw2pkzT5ZJ/41Zycnl5bZTt0REVj0sbGw+uey6I0rT8y483I/xq35zzstkuSLuaI09o087kxCL202yzAAAAAAAAAAAAAAAAAAAAAAAAAPGsghq1aePYrMN622xhNohMxtHcWkKu/wAsu5E1iV6ZrY/7hV3FjUh0bXczmsw7ceel0mkwbmn2Jp2rybRFHQm7yQDxghzN4mpNNvmc1u3tYpiaxL2hZznyi8dyYrMovnpTuVpb2UKW780jSKxDhyZ7ZOvUJZzbJUiNMqVPPsTEItbTaisFmMzt6EAAAAAAAAAAAAAAAAAAAAAAAAAA8aAgqUeqKzDWt/2hawQ07ZRqvk916jas0hNQhHmopFoZ2mekxKgAA1a1KCfE45ZWYhtW1pjW3jq9Ekl6bDaYp+2C3IW6TU6PVkxDO1/0nSwWZPQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGMoJjSYmYQyt+xXTSMianHCJhnadyyJQAAIq1NvkRML0tphG37kaWnJ+k0YJFtM5tMsggAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/2Q==",
      color: theme.palette.primary.light,
    },
  ];

  const isSmallScreen = useMediaQuery("(max-width: 600px)");
  const markerClustererRef = useRef(null);
  const clustererRef = React.useRef(null);
  useEffect(() => {
    // Access the clusterer object when the component mounts
    //const markersInCluster = clusterer.getMarkers();
    if (markerClustererRef.current) {
      console.log("Clusterer:", markerClustererRef.current.getClusterer());
    }
  }, []);

  function detectClustersWithSameCoordinates(markerClustererInstance) {
    const clusters = markerClustererInstance.getClusterer().clusters;

    const clustersWithSameCoordinates = clusters.filter((cluster) => {
      const clusterMarkers = cluster.getMarkers();

      if (clusterMarkers.length < 2) {
        return false; // Ignore clusters with less than 2 markers
      }

      const firstMarkerPosition = clusterMarkers[0].getPosition();

      // Check if all markers in the cluster have the same coordinates
      for (let i = 1; i < clusterMarkers.length; i++) {
        const markerPosition = clusterMarkers[i].getPosition();

        if (!markerPosition.equals(firstMarkerPosition)) {
          return false; // Exit if any marker has different coordinates
        }
      }

      return true; // All markers in the cluster have the same coordinates
    });

    return clustersWithSameCoordinates;
  }

  const handleClustererClick = (clusterer) => {
    console.log(clusterer);
    const newCenter = map.getCenter();
    const NE = map.getBounds().getNorthEast();
    const boundsMap = map.getBounds();
    const SW = map.getBounds().getSouthWest();
    console.log(
      "Novi centar je+" +
        newCenter +
        "NE" +
        NE +
        "SW" +
        SW +
        "bounds " +
        boundsMap
    );
    setSameLocationClusterZoom(null);

    // Log the clusterer when a clusterer is clicked
    const markersInCluster = clusterer.getMarkers(); // Get all markers within the cluster

    const clustersWithSameCoordinates =
      detectClustersWithSameCoordinates(clusterer);

    if (clustersWithSameCoordinates.length > 0 && map.getZoom() === 22) {
      // Handle the case where there are multiple markers with the same coordinates
      /* console.log(
        "Cluster contains markers with the same coordinates:",
        clustersWithSameCoordinates[0].getMarkers()[0].getPosition().lat() +
          "lng" +
          clustersWithSameCoordinates[0].getMarkers()[0].getPosition().lng()
      );*/
      const redoviZaPrikaz = rows.filter(
        (item) =>
          item.longitude ===
            clustersWithSameCoordinates[0]
              .getMarkers()[0]
              .getPosition()
              .lng() &&
          item.latitude ===
            clustersWithSameCoordinates[0].getMarkers()[0].getPosition().lat()
      );
      var filterIDs = [];
      if (redoviZaPrikaz !== undefined && redoviZaPrikaz.length !== 0) {
        redoviZaPrikaz.map((item) => {
          filterIDs.push(item.id.toString());
        });
        const newData = { ...filterParameters };

        // Update the value of the first element
        newData.items[0].columnField = "id";
        newData.items[0].operator = "contains";
        newData.items[0].operatorValue = "isAnyOf";
        newData.items[0].value = filterIDs; // You can modify this value as needed

        // Update the state with the new array

        setFilterParameters(newData);
      }

      dispatch(setSnackbar(true, "info", t("snackbar.sameLocation")));

      setSameLocationClusterZoom(map.getZoom());
    } else {
      // Handle the regular cluster click behavior
      console.log("Regular cluster clicked:", clusterer);
    }
  };

  function handleZoomOut() {
    if (map !== null) {
      console.log("curr zoom" + map.getZoom());
      if (sameLocationClusterZoom !== null && map.getZoom() < 22) {
        setSameLocationClusterZoom(null);
        setFilterParameters({
          items: [
            {
              columnField: "id",
              operator: "contains",
              operatorValue: "contains",
              value: "",
            },
          ],
        });
      }
    }
  }

  return isLoaded ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {props.componentDisplay === "newProject" && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 10,
            position: "relative",
          }}
        >
          {
            <StandaloneSearchBox
              onLoad={onSBLoad}
              onPlacesChanged={onPlacesChanged}
            >
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  setSearchedText(e.target.value);
                }}
                //onBlur={dispatch(setLocationAddress(searchText))}
                placeholder={t("mapbox.addressField")}
                style={{
                  boxSizing: `border-box`,
                  border: `1px solid grey`,
                  width: "100%",
                  height: `50px`,
                  padding: `0 12px`,
                  borderRadius: `3px`,
                  boxShadow: `2px 2px 6px rgba(0, 0, 0, 0.7)`,
                  fontSize: isSmallScreen ? `14px` : `18px`,
                  outline: `none`,
                  textOverflow: `ellipses`,
                  position: "absolute",
                  left: 0,
                  // left: isSmallScreen ? `0%` : "50%",
                  // top: "7px",
                  // marginLeft: isSmallScreen ? `0px` : "-180px",
                }}
              />
            </StandaloneSearchBox>
          }
        </Box>
      )}

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={markerPosition ? markerPosition : config.termoventHQ}
        zoom={10}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{ styles: termovent, streetViewControl: false }}
        onZoomChanged={handleZoomOut}
      >
        {/* Child components, such as markers, info windows, etc. */}
        <></>*
        <Marker
          position={markerPosition ? markerPosition : config.termoventHQ}
          draggable={true}
          onDragEnd={handleMarkerDragEnd}
        />
        {props.componentDisplay === "projectsMap" && (
          <MarkerClusterer
            // minimumClusterSize={3}
            options={options}
            //  calculator={(markers) => clusterCalculator(markers, iconStyles)}
            ref={markerClustererRef}
            onClick={handleClustererClick}
          >
            {(clusterer) => {
              clustererRef.current = clusterer;
              return rows.map((marker) => (
                <Marker
                  key={marker.id}
                  position={{ lat: marker.latitude, lng: marker.longitude }}
                  onClick={() => {
                    setSelectedProject(marker);
                  }}
                  title={t("mapboxproject.clickForMore")}
                  clusterer={clusterer}
                ></Marker>
              ));
            }}
          </MarkerClusterer>
        )}
        {props.componentDisplay === "projectsMap" && selectedProject && (
          <InfoWindow
            position={{
              lat: selectedProject.latitude,
              lng: selectedProject.longitude,
            }}
            onCloseClick={() => {
              setSelectedProject(null);
            }}
          >
            <div>
              <h4>{t("mapboxproject.details")}</h4>
              <p>
                {t("mapboxproject.name")} <b>{selectedProject.name}</b>
              </p>
              <p>
                {t("mapboxproject.country")} <b>{selectedProject.country}</b>
              </p>
              <p>
                {t("mapboxproject.address")} <b>{selectedProject.address}</b>
              </p>
              <p>{selectedProject.description}</p>
              <p>
                {t("mapboxproject.price")} <b>{selectedProject.totalPrice}€</b>
              </p>
              <Button
                onClick={(e) => {
                  toProjectInfo(selectedProject.id);
                }}
                sx={{ color: theme.palette.secondary.main, padding: 0 }}
              >
                {t("mapboxbutton.goToProject")}
              </Button>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      {props.componentDisplay === "newProject" && (
        <Box
          mt={2}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "25px",
          }}
        >
          {/* <TextField
            disabled
            //id="outlined-required"
            label={t("mapbox.address")}
            name="address"
            value={
              (!address?.includes("undefined")
                ? address + ", "
                : address.replaceAll("undefined", "").trim() == ""
                ? ""
                : address.replaceAll("undefined", "").trim() + ", ") +
              (!city?.includes("undefined") ? city + ", " : "") +
              (!country?.includes("undefined") ? country : "")
            }
          /> */}

          <TextField
            //disabled
            //id="outlined-required"
            label={t("mapbox.elevation")}
            name="elevation"
            value={elevation}
            onChange={(e) =>
              dispatch(setElevation({ elevation: e.target.value }))
            }
          />
        </Box>
      )}
    </Box>
  ) : (
    <></>
  );
}
MapboxAPI.whyDidYouRender = true;

export default React.memo(MapboxAPI);
